import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { njCommunities } from "@/data/njCommunities";

const NJCommunitiesCarousel = () => {
  // Duplicate the slides for a seamless infinite loop
  const loop = [...njCommunities, ...njCommunities];

  return (
    <section
      className="py-20 md:py-28 bg-background overflow-hidden"
      aria-label="New Jersey communities lifestyle carousel"
    >
      <div className="container mx-auto max-w-[1400px] px-6 mb-12 md:mb-16 text-center">
        <p className="eyebrow mb-4">The Communities</p>
        <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
          A New Jersey lifestyle{" "}
          <span className="italic text-primary">worth coming home to.</span>
        </h2>
        <p className="mt-5 text-sm md:text-base text-muted-foreground font-light">
          Hover to pause · tap or click a community to explore it in depth.
        </p>
      </div>

      <div className="relative group">
        {/* Edge gradients for premium fade */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10 bg-gradient-to-l from-background to-transparent" />

        <div
          className="flex w-max gap-4 md:gap-6 animate-marquee group-hover:[animation-play-state:paused] motion-reduce:animate-none"
          style={{ animationDuration: "70s" }}
        >
          {loop.map((slide, i) => (
            <Link
              key={`${slide.slug}-${i}`}
              to={`/communities/${slide.slug}`}
              className="group/card relative shrink-0 w-[78vw] sm:w-[420px] md:w-[480px] lg:w-[560px] aspect-[4/3] overflow-hidden bg-secondary focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover/card:scale-105"
                loading="lazy"
                width={1280}
                height={960}
              />
              {/* Base gradient always visible */}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/70 via-transparent to-transparent" />

              {/* Base caption */}
              <figcaption className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-brand-ivory transition-opacity duration-300 group-hover/card:opacity-0">
                <p className="eyebrow-sm mb-2">
                  {slide.caption}
                </p>
                <p className="font-serif text-2xl md:text-3xl font-light">
                  {slide.name}
                </p>
              </figcaption>

              {/* Hover / focus preview card */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 bg-brand-charcoal/80 opacity-0 translate-y-2 transition-all duration-300 group-hover/card:opacity-100 group-hover/card:translate-y-0 group-focus/card:opacity-100 group-focus/card:translate-y-0 text-brand-ivory">
                <p className="eyebrow-sm mb-2">
                  {slide.county}
                </p>
                <p className="font-serif text-2xl md:text-3xl font-light mb-3">
                  {slide.name}
                </p>
                <p className="text-sm md:text-base font-light leading-relaxed text-brand-ivory/85 mb-5 max-w-md">
                  {slide.preview}
                </p>
                <span className="inline-flex items-center gap-2 eyebrow-sm">
                  Explore {slide.name} <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NJCommunitiesCarousel;
